import React, { useContext, useEffect, useState, useRef } from 'react'
import { FronthubContext } from 'front-hub-context-api'
import { CONTEXT } from '../../../../../constants'
import getMfeLoader from '../../../loaders'
import removeMfeStuffs from './removeMfeStuffs'
import mfeDepsVersionValidator from './mfeDepsVersionValidator'
import FronthubError from '../../../../../core/helpers/FronthubError'

/**
 * @param {String} name - The micro frontend name. Can be "mfe-name" or "mfe-name@1.0.1" for example.
 * @param {('high'|'medium')} priority - The priority that the micro frontend will be loaded.
 * @param {String} errorCallback - A callback to be called when an error occurs.
 * @param {Function} renderWrapper - A function to render the wrapper that will be parent of micro frontend.
 * @param {Node} fallback - An alternate UI to render in place of the actual micro frontend if it has not finished loading.
 * @param {Object} props - The properties that will be passed down to the micro frontend itself. It's should be a flat object (not accept any function).
 * @returns {ReactNode} A React element that renders the micro frontend.
 */
const Microfrontend = ({
  name,
  priority = 'high',
  errorCallback,
  renderWrapper,
  fallback = '',
  props = {},
  ...rest
}) => {
  const [hubApi, setHubApi] = useState({})
  const [manifestInstance, setManifestInstance] = useState({})
  const rootRef = useRef()
  const {
    [CONTEXT]: { configuration },
    mfeDependencies,
  } = useContext(FronthubContext)
  const hostName = configuration.name
  const mfeIsLoaded = Boolean(hubApi.renderAt)

  useEffect(() => {
    const { error } = mfeDepsVersionValidator(name, mfeDependencies, hostName)
    if (error) {
      throw new FronthubError(error)
    }

    async function requireMfe() {
      try {
        const loader = getMfeLoader({
          name,
          options: { priority },
          configuration,
          callbacks: {
            onLoadManifest: setManifestInstance,
            onLoadDependency: setHubApi,
          },
        })

        await loader()
      } catch (err) {
        typeof errorCallback === 'function'
          ? errorCallback(err)
          : console.error(err)
      }
    }

    requireMfe()
  }, [])

  useEffect(() => {
    if (mfeIsLoaded && !!rootRef.current) {
      hubApi.renderAt(rootRef.current, props)
    }

    return () => {
      if (!mfeIsLoaded) return

      hubApi.unmount?.()
      removeMfeStuffs(manifestInstance)
    }
  }, [hubApi, props])

  if (!mfeIsLoaded) {
    return fallback
  }

  if (renderWrapper) {
    return renderWrapper({
      children: <div {...rest} ref={rootRef} id={`mfe-${name}`} />,
    })
  }

  return <div {...rest} ref={rootRef} id={`mfe-${name}`} />
}

export default Microfrontend
